import { Suspense, useState } from 'react';
import {
  Await,
  useNavigate,
  useNavigation,
  useParams,
  useSearchParams,
  useRevalidator,
  useLoaderData,
} from 'react-router-dom';
import { captureException } from '@sentry/react';
import { Link, PageHeading, Paragraph } from '@quickmd-llc/design-system';
import { Layout, Loader } from '@components';
import { useApplicationStore } from '@data';
import { Events, tracking } from '@lib';
import { getTimeZone } from '@utils';
import { RescheduleReason } from '@router/utils';
import { Availabilities as ScheduleAvailabilities, Provider } from '@types';
import {
  Availabilities,
  DayPicker,
  Filters,
  FiltersButton,
  FiltersProvider,
  ReservationUnavailableAlert,
  SearchQuery,
  SelectedProviderAvailability,
  useFilters,
} from './components';
import {
  getNextStepForRescheduleReason,
  handleSelectVisit,
  releaseVisitIfReserved,
} from './utils';
import { useShallow } from 'zustand/shallow';
import AccountBlockedAlert from './components/AccountBlockedAlert';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { AvailabilitiesLoader } from '@router/loaders/scheduleLoader';
import { useIsInPersonVisit } from './hooks/useIsInPersonVisit';
import { InPersonVisitAcknowledgmentContent } from './components/InPersonVisitAcknowledgmentContent';
import { IN_PERSON_CLINIC_FULL_ADDRESS } from 'constants/InPersonClinicDetails';
import { useMht } from './hooks/useMht';

function SchedulePage() {
  const { state } = useNavigation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { visitTypeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const reason = searchParams.get('reason') as RescheduleReason | null;
  const { availabilities: availabilitiesPromise } =
    useLoaderData() as AvailabilitiesLoader;
  const { provider, setProvider } = useFilters();
  const { utmParameters, auth, patient } = useApplicationStore(
    useShallow((s) => ({
      setVisitTypeId: s.setVisitTypeId,
      utmParameters: s.utmParameters,
      auth: s.auth,
      patient: s.patient,
    })),
  );

  const { isInPersonVisit, isInPersonVisitRequired } = useIsInPersonVisit();

  const { isSubscribingToMht } = useMht();

  const [
    inPersonVisitAcknowledgmentCompleted,
    setInPersonVisitAcknowledgmentCompleted,
  ] = useState(false);

  const showInPersonVisitAcknowledgment =
    isInPersonVisit && !inPersonVisitAcknowledgmentCompleted;

  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(
    reason === 'dateTimeNotAvailable',
  );
  const [isBlockedAlertOpen, setIsBlockedAlertOpen] = useState<boolean>(false);

  const isLoggedIn = Boolean(auth.accessToken && patient?.patientProfile);
  const date = searchParams.get('date');
  const isLoading = state === 'loading';

  const { description, title } = getHeadingText({
    reason,
    isLoggedIn,
    showInPersonVisitAcknowledgment,
    isInPersonVisitRequired,
    isSubscribingToMht,
  });

  async function handleSelectAvailability(
    providerArg: Provider,
    time: string,
    position?: number,
  ) {
    if (!date || !visitTypeId) {
      return;
    }

    const visitId = searchParams.get('visitId');
    const timeZone = getTimeZone();
    const data = {
      provider: providerArg,
      time,
      visitTypeId,
      date,
      timeZone,
      visitId,
    };

    try {
      await handleSelectVisit(data);

      tracking.trackEvent(Events.SELECT_VISIT, { DoctorPosition: position });

      const next = getNextStepForRescheduleReason(reason, isLoggedIn);
      navigate(next);
    } catch (error) {
      captureException(error);

      if (isAxiosError(error)) {
        if (error.name === 'Forbidden') {
          setIsBlockedAlertOpen(true);
        }
        if (error.name === 'DateTimeNotAvailable') {
          revalidator.revalidate();
          alert(
            'The visit slot you selected was booked before we could reserve it. Please pick another slot.',
          );
        }
      }
    }
  }

  function handleClickNotReadyToBook() {
    tracking.trackEvent(Events.CLICK_NOT_READY_TO_BOOK, {
      ...utmParameters,
      fbpixel: undefined,
    });

    releaseVisitIfReserved();
  }

  return (
    <Layout className="flex flex-col" hideProgressBar={isLoggedIn}>
      {isBlockedAlertOpen && (
        <AccountBlockedAlert handleClose={() => setIsBlockedAlertOpen(false)} />
      )}
      <ReservationUnavailableAlert
        handleClose={() => setIsAlertOpen(false)}
        isOpen={isAlertOpen}
      />

      <div className="mb-sp-2 grid gap-sp-0.5">
        <PageHeading>{title}</PageHeading>
        <Paragraph className="text-text-medium">{description}</Paragraph>
      </div>

      {showInPersonVisitAcknowledgment ? (
        <InPersonVisitAcknowledgmentContent
          setInPersonVisitAcknowledgmentCompleted={
            setInPersonVisitAcknowledgmentCompleted
          }
        />
      ) : (
        <>
          <div className="mb-6 grid gap-4">
            {!isLoggedIn ? (
              <div>
                <Link
                  to={getNextStepForRescheduleReason(reason, false, true)}
                  onClick={handleClickNotReadyToBook}
                >
                  Not ready to book?
                </Link>
              </div>
            ) : isInPersonVisit ? (
              <div className="flex w-full flex-col gap-sp-1 rounded-lg border border-coastal-blue-50 bg-frost-20 p-sp-2">
                <Paragraph className="font-semibold text-text-dark">
                  Showing in-person visits only
                </Paragraph>

                <Paragraph className="text-text-medium" size="sm">
                  {IN_PERSON_CLINIC_FULL_ADDRESS}
                </Paragraph>
              </div>
            ) : null}

            <div className="flex items-center justify-between gap-2">
              <SearchQuery />
              <FiltersButton />
            </div>

            <Filters />

            <DayPicker />
          </div>

          {isLoading ? (
            <Loader />
          ) : (
            <Suspense fallback={<Loader />}>
              <Await resolve={availabilitiesPromise}>
                {({
                  availabilities = [],
                  selectedProvider,
                  availabilityDate,
                }: ScheduleAvailabilities) => {
                  if (selectedProvider) {
                    setProvider(selectedProvider);
                  }
                  if (!date || date !== availabilityDate) {
                    setSearchParams(
                      {
                        date: availabilityDate || dayjs().format('YYYY-MM-DD'),
                      },
                      { replace: true },
                    );
                  }
                  return provider ? (
                    <SelectedProviderAvailability
                      availabilities={availabilities}
                      provider={provider}
                      onSelectAvailability={handleSelectAvailability}
                    />
                  ) : (
                    <Availabilities
                      availabilities={availabilities}
                      onSelectAvailability={handleSelectAvailability}
                    />
                  );
                }}
              </Await>
            </Suspense>
          )}
        </>
      )}
    </Layout>
  );
}

const IN_PERSON_VISIT_REQUIRED_TEXT =
  'As required by your state, an in-person visit is necessary to continue your addiction treatment. Please book your in-person appointment now.';
const IN_PERSON_VISIT_NOT_REQUIRED_TEXT =
  'Annual in-person visits are required to ensure compliance with state regulations. If you have been seen in person in the last 11 months, you may book a virtual visit instead.';

function getHeadingText({
  reason,
  isLoggedIn,
  showInPersonVisitAcknowledgment,
  isInPersonVisitRequired,
  isSubscribingToMht,
}: {
  reason: string | null;
  showInPersonVisitAcknowledgment: boolean;
  isInPersonVisitRequired: boolean;
  isLoggedIn: boolean;
  isSubscribingToMht: boolean;
}) {
  if (showInPersonVisitAcknowledgment) {
    return {
      title: 'In-person visit required',
      description: isInPersonVisitRequired
        ? IN_PERSON_VISIT_REQUIRED_TEXT
        : IN_PERSON_VISIT_NOT_REQUIRED_TEXT,
    };
  }
  if (reason === 'dateTimeNotAvailable' || reason === 'rescheduleVisit') {
    return {
      title: 'Reschedule your visit',
      description:
        reason === 'dateTimeNotAvailable'
          ? 'Sorry, the visit slot you selected was booked before we could reserve it. Please pick another slot.'
          : 'Find a time that fits your schedule.',
    };
  }

  return {
    title: 'Schedule your visit',
    description: isSubscribingToMht
      ? 'Your membership starts once you book your visit. During your visit, your doctor will discuss your symptoms and review treatment options, including medication when appropriate.'
      : isLoggedIn
        ? 'Book a follow-up to continue your care.'
        : 'Find a time that fits your schedule.',
  };
}

export default function ProvidedSchedulePage() {
  return (
    <FiltersProvider>
      <SchedulePage />
    </FiltersProvider>
  );
}
